import {
  type ContractConfig,
  emptyContractConfig,
  readContractConfig,
  contractConfigToPayload,
  missingRequiredContractKeys,
} from './contractSourceConfig'

/**
 * Edytowalna część źródła integracji wspólna dla tworzenia i edycji: klucze `config`
 * (auth, encja docelowa, intake contract) + CORS patterns. Identyfikacja (code/authType)
 * jest niezmienna i żyje poza draftem.
 */
export type SourceConfigDraft = {
  authHeaderName: string
  authSecretEnvVar: string
  targetEntityType: string // 'customer' | 'contract' | ''
  patternsText: string
  contract: ContractConfig
}

export function emptySourceConfigDraft(): SourceConfigDraft {
  return {
    authHeaderName: 'X-Internal-Api-Key',
    authSecretEnvVar: '',
    targetEntityType: 'customer',
    patternsText: '',
    contract: emptyContractConfig(),
  }
}

/** Buduje draft z istniejącego configu źródła + listy patterns (na potrzeby edycji). */
export function readSourceConfigDraft(config: Record<string, unknown>, patterns: string[]): SourceConfigDraft {
  const str = (k: string) => (typeof config[k] === 'string' ? (config[k] as string) : '')
  return {
    authHeaderName: str('auth_header_name'),
    authSecretEnvVar: str('auth_secret_env_var'),
    targetEntityType: str('target_entity_type'),
    patternsText: patterns.join('\n'),
    contract: readContractConfig(config),
  }
}

/**
 * Składa obiekt `config` + listę patterns z draftu. Zwraca `error` (string) gdy walidacja
 * pól wymaganych contract nie przejdzie, inaczej `error: null`.
 */
export function buildSourceConfig(draft: SourceConfigDraft): {
  config: Record<string, unknown>
  patterns: string[]
  error: string | null
} {
  if (draft.targetEntityType === 'contract') {
    const missing = missingRequiredContractKeys(draft.contract)
    if (missing.length > 0) {
      return { config: {}, patterns: [], error: 'Uzupełnij wymagane pola intake umowy: ' + missing.join(', ') }
    }
  }

  const config: Record<string, unknown> = {}
  if (draft.authHeaderName.trim()) config.auth_header_name = draft.authHeaderName.trim()
  if (draft.authSecretEnvVar.trim()) config.auth_secret_env_var = draft.authSecretEnvVar.trim()
  if (draft.targetEntityType.trim()) config.target_entity_type = draft.targetEntityType.trim()

  const patterns = draft.patternsText.split('\n').map(s => s.trim()).filter(Boolean)
  if (patterns.length > 0) config.allowed_origin_patterns = patterns

  if (draft.targetEntityType === 'contract') {
    Object.assign(config, contractConfigToPayload(draft.contract))
  }

  return { config, patterns, error: null }
}
